import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { differenceInSeconds, isAfter } from 'date-fns';
import { atom, useAtomValue } from 'jotai';

import { marketsBunja } from '@flipster/r230522/original/market';
import { convertDate } from '@flipster/utils/datetime';

import { SymbolScope } from '../symbol/bunja';

export interface PauseDetail {
  isPaused: boolean;
  status: 'None' | 'Warn' | 'Scheduled' | 'Paused';
  pauseDuration?: number;
  untilPauseStart?: number;
  untilPauseEnd?: number;
}

export const tradingPauseBunja = bunja(() => {
  const symbol = bunja.use(SymbolScope);
  const { marketsAtom } = bunja.use(marketsBunja);

  const pauseDetailAtom = atom<PauseDetail>((get) => {
    const market = get(marketsAtom);
    const fundingTimeBlocking = market?.[symbol]?.fundingTimeBlocking;

    if (!fundingTimeBlocking || 'None' in fundingTimeBlocking) {
      return { isPaused: false, status: 'None' };
    }

    if ('Likely' in fundingTimeBlocking) {
      return {
        isPaused: false,
        status: 'Warn',
        pauseDuration: fundingTimeBlocking.Likely.interval,
      };
    }

    const { from, until } = fundingTimeBlocking.Scheduled;
    const fromDate = convertDate(from)!;
    const untilDate = convertDate(until)!;
    const now = new Date();
    const pauseDuration = differenceInSeconds(untilDate, fromDate, { roundingMethod: 'ceil' });

    const isPaused = isAfter(now, fromDate);

    if (isPaused) {
      return {
        isPaused: true,
        status: 'Paused',
        pauseDuration,
        untilPauseEnd: differenceInSeconds(untilDate, new Date(), { roundingMethod: 'ceil' }),
      };
    }

    return {
      isPaused: false,
      status: 'Scheduled',
      pauseDuration,
      untilPauseStart: differenceInSeconds(fromDate, new Date(), { roundingMethod: 'ceil' }),
    };
  });

  // pauseDetailAtom은 매 tick 새 객체라 구독 시 리렌더가 잦다. isPaused만 필요한 소비처는
  // 이 boolean atom을 구독하면 값이 실제로 바뀔 때만(정지 상태 전환) 전파된다.
  const isPausedAtom = atom((get) => get(pauseDetailAtom).isPaused);

  return { pauseDetailAtom, isPausedAtom };
});
tradingPauseBunja.debugLabel = 'tradingPauseBunja';

export function useTradingPause(symbol: string): PauseDetail {
  const { pauseDetailAtom } = useBunja(tradingPauseBunja, [SymbolScope.bind(symbol)]);
  return useAtomValue(pauseDetailAtom);
}
